import { useEffect, useState } from "react";
import { ImageOff, MessageCircle, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { imgSrc } from "@/lib/api";
import { LINKS, productEnquiry } from "@/lib/store";
import { StatusBadge } from "./StatusBadge";

const Gallery = ({ images, name }) => {
  const [i, setI] = useState(0);
  if (!images?.length) {
    return <div className="grid aspect-square place-items-center rounded-xl bg-[#F4EEE2] text-[#8B5A2B]/60"><ImageOff className="h-10 w-10" /></div>;
  }
  return (
    <div>
      <div className="aspect-square overflow-hidden rounded-xl bg-[#F4EEE2]">
        <img src={imgSrc(images[i].url)} alt={name} className="h-full w-full object-cover" data-testid="product-modal-main-image" />
      </div>
      {images.length > 1 && (
        <div className="no-scrollbar mt-3 flex gap-2 overflow-x-auto">
          {images.map((im, idx) => (
            <button key={im.id} onClick={() => setI(idx)} data-testid={`product-modal-thumb-${idx}`}
                    className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 ${idx === i ? "border-[#C59B27]" : "border-transparent opacity-70"}`}>
              <img src={imgSrc(im.url)} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Specs = ({ p }) => {
  const rows = [["Material", p.material], ["Size", p.size], ["Dimensions", p.dimensions], ["SKU", p.sku],
    ...Object.entries(p.specifications || {})].filter(([, v]) => v);
  if (!rows.length) return null;
  return (
    <dl className="mt-5 divide-y divide-[#E5E0D8] rounded-xl border border-[#E5E0D8]" data-testid="product-modal-specs">
      {rows.map(([k, v]) => (
        <div key={k} className="grid grid-cols-5 gap-3 px-4 py-2.5 text-sm">
          <dt className="col-span-2 text-[#5B625D]">{k}</dt><dd className="col-span-3 font-medium text-[#1F2421]">{v}</dd>
        </div>
      ))}
    </dl>
  );
};

const ChipGroup = ({ label, options, value, onChange, testPrefix }) => {
  if (!options?.length) return null;
  return (
    <div className="mt-5">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#5B625D]">{label}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((v) => (
          <button key={v} onClick={() => onChange(v === value ? null : v)} aria-pressed={v === value} data-testid={`${testPrefix}-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`min-h-10 rounded-full border px-4 text-sm font-medium transition-colors ${v === value ? "border-[#1B4332] bg-[#1B4332] text-white" : "border-[#E5E0D8] bg-white text-[#1F2421] hover:border-[#1B4332]"}`}>{v}</button>
        ))}
      </div>
    </div>
  );
};

export const ProductModal = ({ product, open, onOpenChange }) => {
  const [variant, setVariant] = useState(null);
  const [brand, setBrand] = useState(null);
  useEffect(() => { setVariant(null); setBrand(null); }, [product?.id]);
  if (!product) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-4xl overflow-y-auto rounded-2xl bg-[#FDFBF7] p-4 sm:p-6" data-testid="product-modal">
        <div className="grid gap-6 md:grid-cols-2">
          <Gallery key={product.id} images={product.images} name={product.name} />
          <div className="flex flex-col">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#8B5A2B]">{product.category}</span>
              <StatusBadge status={product.availability_status} testId="product-modal-status" />
              {product.is_demo && <span className="rounded-full bg-[#10291F] px-2 py-0.5 text-[10px] font-bold uppercase text-[#F3DB8C]">Sample listing</span>}
            </div>
            <DialogTitle className="mt-2 font-display text-2xl sm:text-3xl font-bold text-[#1B4332]" data-testid="product-modal-title">{product.name}</DialogTitle>
            <DialogDescription className="mt-3 text-sm sm:text-base leading-relaxed text-[#3E4540]">{product.description || "Ask us on WhatsApp for full details."}</DialogDescription>
            <ChipGroup label="Choose brand" options={product.brands} value={brand} onChange={setBrand} testPrefix="brand-chip" />
            <ChipGroup label="Choose size / variant" options={product.variants} value={variant} onChange={setVariant} testPrefix="variant-chip" />
            {(brand || variant) && (
              <p className="mt-4 rounded-xl bg-[#E6F2EA] px-4 py-2 text-sm font-semibold text-[#1B4332]" data-testid="selection-summary">Your choice: {[brand, variant].filter(Boolean).join(" · ")}</p>
            )}
            <Specs p={product} />
            <p className="mt-5 text-xs text-[#5B625D]">Prices and exact stock are confirmed personally on WhatsApp.</p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <a href={productEnquiry(product, variant, brand)} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-enquiry-btn"
                 className="inline-flex h-12 shrink-0 sm:flex-1 items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 font-semibold text-[#0B3B1F] shadow-md transition-transform hover:-translate-y-0.5">
                <MessageCircle className="h-5 w-5" /> Enquire on WhatsApp
              </a>
              <a href={LINKS.call} data-testid="product-modal-call-btn" className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-[#1B4332]/30 px-6 font-semibold text-[#1B4332] hover:bg-[#F4EEE2]">
                <Phone className="h-4 w-4" /> Call
              </a>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
